// The pin for a club event: the club's own logo in a white disc, on the
// building it is happening in, with when it is printed underneath.
//
// A raw google.maps.Marker cannot draw this. A Marker's icon has to be a URL,
// so a logo would have to be inlined into an SVG data URL — and a data-URL SVG
// is an isolated document whose external `<image href="https://…">` browsers
// refuse to load, which renders every pin as an empty white circle. A real
// <img> in a real div has none of that problem, and it brings CSS with it: the
// ring, the shadow and the hover are one class each.
//
// The disc is what makes forty of these legible at once: a club's logo is
// whatever shape it uploaded — square, wide, transparent — and cropped into a
// circle they read as one set of pins instead of as clutter.
//
// Lazily constructed, like every overlay in this folder: google.maps.OverlayView
// does not exist until the Maps script has loaded.

/**
 * @param map       google.maps.Map
 * @param position  {lat, lng} — where the event is
 * @param logo      absolute URL of the club's logo, or '' for a plain disc
 * @param title     the event name, for the hover tooltip
 * @param when      the short line under the disc — "Sep 8" or "8:00 PM"
 * @param tone      '' | 'today' | 'now', for the badge's colour
 * @param onTap     click handler
 * @returns {{ remove: () => void }}
 */
export function createEventPinOverlay(map, position, { logo, title, when, tone, onTap }) {
  const gmaps = window.google.maps;
  const latLng = new gmaps.LatLng(position.lat, position.lng);

  class EventPin extends gmaps.OverlayView {
    onAdd() {
      const div = document.createElement('div');
      div.className = 'pfa-eventpin';
      div.title = title || '';

      const disc = document.createElement('div');
      disc.className = 'pfa-eventpin-disc';
      if (logo) {
        const img = document.createElement('img');
        img.src = logo;
        img.alt = '';
        // A logo that 404s leaves the plain disc rather than a broken-image icon.
        img.addEventListener('error', () => img.remove());
        disc.appendChild(img);
      }
      div.appendChild(disc);

      if (when) {
        const badge = document.createElement('span');
        badge.className = `pfa-eventpin-when${tone ? ` ${tone}` : ''}`;
        badge.textContent = when;
        div.appendChild(badge);
      }

      // The map must not also act on this tap: without stopping it, tapping a
      // pin sitting over a room polygon opens the room card behind the sheet.
      ['mousedown', 'touchstart', 'pointerdown'].forEach(t =>
        div.addEventListener(t, e => e.stopPropagation()));
      div.addEventListener('click', (e) => { e.stopPropagation(); onTap?.(); });

      this.div = div;
      // overlayMouseTarget is the pane that receives clicks; the drawing panes
      // above it do not, so a pin placed there would look right and be inert.
      this.getPanes().overlayMouseTarget.appendChild(div);
    }
    draw() {
      if (!this.div) return;
      const pt = this.getProjection()?.fromLatLngToDivPixel(latLng);
      if (!pt) return;
      this.div.style.left = `${pt.x}px`;
      this.div.style.top = `${pt.y}px`;
    }
    onRemove() { this.div?.remove(); this.div = null; }
  }

  const overlay = new EventPin();
  overlay.setMap(map);
  return { remove: () => overlay.setMap(null) };
}
